import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import useCallbackRef from 'hooks/useCallbackRef';
import utils, { toggleValueInArray } from 'utils';
import FilterActionWrapper from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/filters/FilterActionWrapper';
import BriefcaseIcon from 'icons/Briefcase.icon';
import { Button, Checkbox } from 'ncoded-component-library';
import { ReportFilterType } from '../../../../types';
import useQueryFilter from 'hooks/useQueryFilter';
import { DATA_ROOM_MEMBER_ROLES } from 'router/subrouters/DataRoom/models/DataRoomMember';
import { NAVIGATION_VARIANTS, NavigationVariants } from 'models/Navigation';

import './RoleFilter.styles.scss';
import './RoleFilter.styles.responsive.scss';

type RoleFilterProps = {
  className?: string;
  variant?: NavigationVariants;
};

const FILTER_KEY = 'memberRole';

const RoleFilter: React.FC<RoleFilterProps> = (props) => {
  const { className, variant = NAVIGATION_VARIANTS.SECONDARY } = props;

  const { t } = useTranslation();

  const roleOptions = useMemo(
    () => [
      {
        label: t('owner'),
        value: DATA_ROOM_MEMBER_ROLES.OWNER,
      },
      {
        label: t('administrator'),
        value: DATA_ROOM_MEMBER_ROLES.ADMIN,
      },
      {
        label: t('publisher'),
        value: DATA_ROOM_MEMBER_ROLES.PUBLISHER,
      },
      {
        label: t('downloader'),
        value: DATA_ROOM_MEMBER_ROLES.VIEWER_DOWNLOADER,
      },
      {
        label: t('viewer'),
        value: DATA_ROOM_MEMBER_ROLES.VIEWER,
      },
    ],
    [t],
  );

  const {
    params: { memberRole },
    onFiltersApply,
  } = useQueryFilter<ReportFilterType>();

  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>(null);
  const defaultVal = useMemo(
    () => (memberRole ? utils.mapParamsToArray(memberRole) : []),
    [memberRole],
  );
  const [currentState, setCurrentState] = useState(defaultVal);

  const classes = classNames('role-filter', className);

  const onSelectFilters = useCallback(
    (options: string[]) => {
      dropdown.setIsOpen(false);

      const patchedVal = utils.multiSelectValueToStringArray(options as any);

      onFiltersApply(FILTER_KEY, patchedVal);
    },
    [dropdown, onFiltersApply],
  );

  const triggerTitle = useMemo(
    () => (memberRole ? t('openToSeeRoleFilters') : t('role')),
    [memberRole, t],
  );

  useEffect(() => {
    setCurrentState(defaultVal);
  }, [defaultVal]);

  return (
    <FilterActionWrapper
      ref={dropdownRef}
      className={classes}
      trigerTitle={triggerTitle}
      icon={BriefcaseIcon}
      variant={variant}
      onClose={() => {
        setCurrentState(defaultVal);
      }}
    >
      <div>
        {roleOptions.map(({ value, label }) => (
          <Checkbox
            key={value}
            label={label}
            checked={currentState.some((el) => el === value)}
            onChange={() => {
              setCurrentState((prev) => toggleValueInArray(prev, value));
            }}
          />
        ))}
      </div>

      <div className="filter-action-wrapper__buttons">
        <Button variant="link" onClick={() => dropdown?.setIsOpen(false)}>
          {t('cancel')}
        </Button>
        <Button
          variant="outline"
          disabled={currentState === defaultVal}
          onClick={() => onSelectFilters(currentState)}
        >
          {t('applyRoleFilter')}
        </Button>
      </div>
    </FilterActionWrapper>
  );
};

export default RoleFilter;
